import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, MessageCircle, Send } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation } from 'react-router';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Feedback';
import { CheckboxField, SelectField, TextAreaField, TextField } from '@/components/ui/Field';
import { useCategories } from '@/features/catalog/api';
import { useSite } from '@/features/site/SiteProvider';
import { api, ApiError } from '@/lib/api';
import { TURNSTILE_SITE_KEY, whatsappLink } from '@/lib/utils';
import type { InquiryCreated } from '@/types/api';
import { inquiryFormSchema, type InquiryFormValues } from './schema';
import { Turnstile } from './Turnstile';

export const OTHER_CATEGORY = 'other';

export interface InquiryContext {
  /** Pre-selected category slug (e.g. on a category or product page). */
  categorySlug?: string;
  /** Product the visitor is asking about. */
  product?: { slug: string; name: string };
  /** Pre-filled customisation text, e.g. "Private label" on the OEM page. */
  customization?: string;
}

interface InquiryFormProps {
  context?: InquiryContext;
  title?: string;
  description?: string;
}

function newKey() {
  return crypto.randomUUID();
}

export function InquiryForm({ context = {}, title = 'Request a Quote', description }: InquiryFormProps) {
  const { pathname } = useLocation();
  const { contact } = useSite().settings;
  const categories = useCategories();
  const startedAt = useRef(Date.now());
  const idempotencyKey = useRef(newKey());
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaReset, setCaptchaReset] = useState(0);
  const onCaptchaToken = useCallback((t: string | null) => setCaptchaToken(t), []);

  const defaults = useMemo<InquiryFormValues>(
    () => ({
      fullName: '',
      companyName: '',
      phone: '',
      email: '',
      country: '',
      category: context.categorySlug ?? '',
      productReference: context.product?.name ?? '',
      quantity: '',
      customization: context.customization ?? '',
      message: '',
      privacyAccepted: false,
      website: '',
    }),
    [context.categorySlug, context.product?.name, context.customization],
  );

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm<InquiryFormValues>({ resolver: zodResolver(inquiryFormSchema), defaultValues: defaults, mode: 'onTouched' });

  // The <select> cannot hold the pre-selected category until its <option> exists, so apply the
  // page context once categories have loaded (unless the visitor already chose something).
  useEffect(() => {
    if (!categories.isSuccess) return;
    const current = getValues('category') || context.categorySlug;
    if (current) setValue('category', current, { shouldDirty: false });
  }, [categories.isSuccess, context.categorySlug, getValues, setValue]);

  const mutation = useMutation({
    mutationFn: (values: InquiryFormValues) =>
      api<InquiryCreated>('/inquiries', {
        method: 'POST',
        body: {
          ...values,
          productSlug: context.product && values.productReference === context.product.name ? context.product.slug : undefined,
          sourcePage: pathname.slice(0, 200),
          idempotencyKey: idempotencyKey.current,
          formStartedAt: startedAt.current,
          turnstileToken: captchaToken ?? undefined,
        },
      }),
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'VALIDATION_ERROR') {
        for (const d of err.details) {
          if (d.path in defaults) setError(d.path as keyof InquiryFormValues, { message: d.message });
        }
      }
      if (TURNSTILE_SITE_KEY) setCaptchaReset((n) => n + 1);
    },
  });

  const startOver = () => {
    idempotencyKey.current = newKey();
    startedAt.current = Date.now();
    reset(defaults);
    mutation.reset();
  };

  if (mutation.isSuccess) {
    return (
      <div className="rounded-[8px] border border-line bg-white p-8 text-center shadow-[var(--shadow-card)]" role="status" aria-live="polite">
        <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-accent-soft text-accent">
          <CheckCircle2 className="h-6 w-6" aria-hidden />
        </div>
        <h3 className="mt-4 text-[18px] font-bold">Thank you — your inquiry has been received</h3>
        <p className="mt-2 text-[14px] text-ink-soft">
          Your reference number is <strong className="font-mono text-ink">{mutation.data.reference}</strong>. Please quote it in any follow-up.
        </p>
        {contact.responseTime && <p className="mt-1 text-[13px] text-muted">{contact.responseTime}</p>}
        <div className="mt-5 flex flex-wrap justify-center gap-3">
          <Button variant="outline" onClick={startOver}>
            Send another inquiry
          </Button>
          {contact.whatsappNumber && (
            <a
              href={whatsappLink(contact.whatsappNumber, `Hello, I just submitted inquiry ${mutation.data.reference}.`)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-11 items-center gap-2 rounded-[6px] border border-line-strong px-5 text-[14px] font-semibold hover:border-ink"
            >
              <MessageCircle className="h-4 w-4 text-whatsapp" aria-hidden /> Follow up on WhatsApp
            </a>
          )}
        </div>
      </div>
    );
  }

  const serverError =
    mutation.error && !(mutation.error instanceof ApiError && mutation.error.code === 'VALIDATION_ERROR')
      ? mutation.error instanceof ApiError
        ? mutation.error.message
        : 'Something went wrong. Please try again.'
      : null;

  const apparel = categories.data?.filter((c) => c.division === 'APPAREL') ?? [];
  const sports = categories.data?.filter((c) => c.division === 'SPORTS') ?? [];
  const captchaRequired = Boolean(TURNSTILE_SITE_KEY);

  return (
    <form onSubmit={handleSubmit((v) => mutation.mutate(v))} noValidate aria-labelledby="inquiry-form-title" className="rounded-[8px] border border-line bg-white p-6 shadow-[var(--shadow-card)] sm:p-8">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id="inquiry-form-title" className="font-display text-[21px] font-bold tracking-tight text-primary">
          {title}
        </h3>
        <span className="rounded-[4px] bg-surface px-2.5 py-1 text-[11px] font-semibold tracking-[0.12em] text-muted uppercase">B2B only · No login needed</span>
      </div>
      {description && <p className="mt-1.5 text-[13px] text-muted">{description}</p>}

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <TextField label="Full name" required autoComplete="name" {...register('fullName')} error={errors.fullName?.message} />
        <TextField label="Company / brand" autoComplete="organization" {...register('companyName')} error={errors.companyName?.message} />
        <TextField
          label="WhatsApp / phone"
          required
          type="tel"
          autoComplete="tel"
          placeholder="+44 20 7946 0000"
          {...register('phone')}
          error={errors.phone?.message}
        />
        <TextField label="Email" required type="email" autoComplete="email" {...register('email')} error={errors.email?.message} />
        <TextField label="Country" autoComplete="country-name" {...register('country')} error={errors.country?.message} />
        <SelectField label="Product category" required {...register('category')} error={errors.category?.message} disabled={categories.isLoading}>
          <option value="">{categories.isLoading ? 'Loading categories…' : 'Choose a category'}</option>
          {apparel.length > 0 && (
            <optgroup label="Apparel — Faisalabad">
              {apparel.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.name}
                </option>
              ))}
            </optgroup>
          )}
          {sports.length > 0 && (
            <optgroup label="Sports goods — Sialkot">
              {sports.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.name}
                </option>
              ))}
            </optgroup>
          )}
          <option value={OTHER_CATEGORY}>Multiple / other products</option>
        </SelectField>
        <TextField label="Product name or reference" {...register('productReference')} error={errors.productReference?.message} placeholder="e.g. Heavyweight T-Shirt" />
        <TextField label="Quantity required" {...register('quantity')} error={errors.quantity?.message} placeholder="e.g. 300 pcs, 50 per size" />
        <TextAreaField
          wrapperClassName="sm:col-span-2"
          label="Customisation / private-label requirements"
          rows={3}
          {...register('customization')}
          error={errors.customization?.message}
          placeholder="Logo placement, printing or embroidery, labels, packaging…"
        />
        <TextAreaField
          wrapperClassName="sm:col-span-2"
          label="Message & specifications"
          required
          rows={5}
          {...register('message')}
          error={errors.message?.message}
          placeholder="Fabric, GSM, sizes, colours, target delivery date…"
          hint="You can share design files by email or WhatsApp after we reply."
        />
      </div>

      {/* Honeypot: hidden from people and assistive tech; bots tend to fill every field. */}
      <div aria-hidden="true" className="absolute -left-[10000px] h-px w-px overflow-hidden">
        <label htmlFor="inquiry-website">Website</label>
        <input id="inquiry-website" type="text" tabIndex={-1} autoComplete="off" {...register('website')} />
      </div>

      <CheckboxField
        className="mt-4"
        {...register('privacyAccepted')}
        error={errors.privacyAccepted?.message}
        label={
          <>
            I agree that my details are used to respond to this inquiry, as described in the{' '}
            <Link to="/privacy-policy" className="font-semibold text-primary underline" target="_blank">
              privacy policy
            </Link>
            .
          </>
        }
      />

      {captchaRequired && (
        <div className="mt-4">
          <Turnstile siteKey={TURNSTILE_SITE_KEY} onToken={onCaptchaToken} resetSignal={captchaReset} />
        </div>
      )}

      {serverError && (
        <Alert tone="error" className="mt-4" title="Your inquiry was not sent">
          {serverError}
        </Alert>
      )}

      <div className="mt-5 flex flex-wrap gap-3">
        <Button
          variant="accent"
          type="submit"
          size="lg"
          className="flex-1 sm:flex-none"
          loading={mutation.isPending}
          disabled={captchaRequired && !captchaToken}
          icon={<Send className="h-4 w-4" aria-hidden />}
        >
          {mutation.isPending ? 'Sending…' : 'Send inquiry'}
        </Button>
        {contact.whatsappNumber && (
          <a
            href={whatsappLink(contact.whatsappNumber)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-[52px] items-center gap-2 rounded-[6px] border border-line-strong bg-white px-5 text-[14px] font-semibold hover:border-ink"
          >
            <MessageCircle className="h-4 w-4 text-whatsapp" aria-hidden /> WhatsApp
          </a>
        )}
      </div>
    </form>
  );
}
